import { BitkitAvatar, BitkitTooltip, IconBuildEnvSetup, rem } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Skeleton } from '@chakra-ui/react/skeleton';
import { useCallback, useMemo, useState } from 'react';

import Clock from '@/components/Clock/Clock';
import LogViewer from '@/components/LogViewer/LogViewer';
import { segmentTrack } from '@/hooks/analytics/useTracking';
import { useStepLogChunks } from '@/pages/BuildDetailsPage/components/StructuredBuildLog/hooks/useStepLogChunks';

import { BuildEnvironmentSetup, StepSummaryStatus } from '../../types/BuildLogSummary';
import { connectorLineStyle } from './StepList';
import { StepNotifications } from './StepListItem';
import StepLogCard from './StepLogCard';
import { getStepStatusDecorator, wrapWithAnsiColor } from './utils/log.utils';

type BuildEnvironmentLogProps = {
  buildEnvironmentSetup: BuildEnvironmentSetup;
  buildSlug: string;
  projectSlug: string;
  viewedAsPublicPage: boolean;
};

const ButtonContent = ({ setup }: { setup: BuildEnvironmentSetup }) => {
  const statusProps = getStepStatusDecorator(setup.status);
  const StatusIcon = statusProps.icon;
  return (
    <Box display="flex" gap="12" alignItems="center" width="100%" paddingInlineEnd="16">
      <BitkitTooltip text={statusProps.text}>
        <StatusIcon size="24" color={statusProps.textColor} aria-label={statusProps.text} />
      </BitkitTooltip>
      <BitkitAvatar variant="icon" icon={IconBuildEnvSetup} />
      Build environment setup
      {setup.startedAt && setup.finishedAt && (
        <Clock marginInlineStart="auto" startDate={new Date(setup.startedAt)} endDate={new Date(setup.finishedAt)} />
      )}
    </Box>
  );
};

const BuildEnvironmentLog = (props: BuildEnvironmentLogProps) => {
  const { buildEnvironmentSetup, buildSlug, projectSlug, viewedAsPublicPage } = props;

  const [isExpanded, setIsExpanded] = useState(buildEnvironmentSetup.status === StepSummaryStatus.Failed);

  const { chunks, isLoading, isSuccess, isError, isDeleted, refetch } = useStepLogChunks({
    uuid: 'build-environment-setup',
    appSlug: projectSlug,
    buildSlug,
    viewedAsPublicPage,
    enabled: isExpanded,
  });

  const logText = useMemo(() => chunks.map(wrapWithAnsiColor).join(''), [chunks]);

  const onScrollNavigate = useCallback(
    (direction: 'up' | 'down') =>
      segmentTrack(`Expand Step Log Scroll Navigation Clicked`, {
        app_slug: projectSlug,
        build_slug: buildSlug,
        scroll_direction: direction,
      }),
    [projectSlug, buildSlug],
  );

  return (
    <StepLogCard
      css={connectorLineStyle}
      expanded={isExpanded}
      header={<ButtonContent setup={buildEnvironmentSetup} />}
      marginBlockEnd="8"
      onExpandedChange={setIsExpanded}
    >
      <StepNotifications retry={refetch} status={isError && isDeleted ? 'deleted' : isError ? 'error' : undefined} />
      {isLoading && <Skeleton height={rem(256)} />}
      {isSuccess && chunks.length > 0 && <LogViewer content={logText} onScrollNavigate={onScrollNavigate} />}
    </StepLogCard>
  );
};

export default BuildEnvironmentLog;
